import { useMemo, useState } from "react";
import { read, utils } from "xlsx";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Calendar, Globe, Settings } from "lucide-react";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { logError } from "@/lib/structured-log";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import type { BlacklistEntry, BlacklistListResponse } from "@/server/blacklist";
import {
  createBlacklistServer,
  deleteBlacklistServer,
  listBlacklistsServer,
  batchCreateBlacklistServer,
} from "@/server/blacklist";
import { extractApiErrorMessage } from "@/lib/api/error";
import { toast } from "sonner";

interface LoaderData {
  workspaceId: string;
  blacklist: BlacklistListResponse;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/workspace/blacklist")({
  loader: async ({ context, parentMatchPromise }): Promise<LoaderData> => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "settings.workspace.blacklist",
      parentMatchPromise,
    );

    const blacklist = await listBlacklistsServer({
      data: {
        workspaceId,
        page: 1,
        pageSize: 50,
      },
    });

    return { workspaceId, blacklist };
  },
  component: BlacklistPage,
});

function BlacklistPage() {
  const { t } = useTranslation(["settings"]);
  const { workspaceId, blacklist } = Route.useLoaderData() as LoaderData;

  const [entries, setEntries] = useState<BlacklistEntry[]>(blacklist.items ?? []);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BlacklistEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const formSchema = useMemo(
    () =>
      z.object({
        company_name: z.string().min(1, t("workspace.blacklistPage.addDialog.companyNameRequired")),
        website: z
          .string()
          .optional()
          .refine(
            (value) => !value || /^https?:\/\/.+/i.test(value),
            t("workspace.blacklistPage.addDialog.websiteInvalid"),
          ),
      }),
    [t],
  );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: "",
      website: "",
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const entry = await createBlacklistServer({
        data: {
          workspaceId,
          company_name: values.company_name.trim(),
          website: values.website?.trim() || null,
        },
      });

      setEntries((prev) => [entry, ...prev]);
      toast.success(t("workspace.blacklistPage.addDialog.success"));
      form.reset();
      setOpen(false);
    } catch (error) {
      const message = extractApiErrorMessage(error, t("workspace.blacklistPage.addDialog.error"));
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  });

  const formatDate = (value: string): string => {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // Use header: 1 to get raw array of arrays
      const rawRows = utils.sheet_to_json<unknown[]>(worksheet, { header: 1 });

      if (rawRows.length === 0) {
        toast.error(t("workspace.blacklistPage.batchImport.noData"));
        setIsImporting(false);
        return;
      }

      const items: { company_name: string; website: string | null }[] = [];

      // Fixed format: Col 0 = Website, Col 1 = Company Name
      for (const row of rawRows) {
        if (!Array.isArray(row) || row.length === 0) continue;

        const website = row[0];
        const name = row[1];

        if (name && String(name).trim()) {
          items.push({
            company_name: String(name).trim(),
            website: website ? String(website).trim() : null,
          });
        }
      }

      if (items.length === 0) {
        toast.error(t("workspace.blacklistPage.batchImport.noData"));
        setIsImporting(false); // Make sure to reset state
        return;
      }

      await batchCreateBlacklistServer({
        data: {
          workspaceId,
          items,
        },
      });

      // Refresh list
      const updatedList = await listBlacklistsServer({
        data: { workspaceId, page: 1, pageSize: 50 },
      });
      setEntries(updatedList.items);

      toast.success(t("workspace.blacklistPage.batchImport.success", { count: items.length }));
    } catch (error) {
      logError("workspace.blacklist.batch_import.failed", {
        workspace_id: workspaceId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      toast.error(t("workspace.blacklistPage.batchImport.error"));
    } finally {
      setIsImporting(false);
      event.target.value = ""; // Reset input
    }
  };

  return (
    <div className="p-6">
      <div className="w-full">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-[16px] font-semibold text-gray-900 mb-2">
              {t("workspace.blacklistPage.title")}
            </h2>
            <p className="text-sm text-muted-foreground">{t("workspace.blacklistPage.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-black text-white hover:bg-black/90"
              onClick={() => setOpen(true)}
            >
              {t("workspace.blacklistPage.addDialog.title")}
            </Button>
            <div className="relative cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={handleFileUpload}
                disabled={isImporting}
                title={t("workspace.blacklistPage.batchImport.title")}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-2 cursor-pointer"
                disabled={isImporting}
              >
                {isImporting ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {t("workspace.blacklistPage.batchImport.importing")}
                  </>
                ) : (
                  t("workspace.blacklistPage.batchImport.title")
                )}
              </Button>
            </div>
          </div>
        </div>
        <div className="w-full rounded-xl border border-[#dfdfdf] bg-background overflow-hidden">
          <Table>
            <TableHeader className="bg-[#f6f7f8]">
              <TableRow className="border-b border-[#dfdfdf] hover:bg-[#f6f7f8]">
                <TableHead className="h-10 px-4">
                  <span className="inline-flex items-center gap-2 text-sm font-normal text-black">
                    <Building2 className="size-4 text-muted-foreground" />
                    {t("workspace.blacklistPage.table.companyName")}
                  </span>
                </TableHead>
                <TableHead className="h-10 px-4 w-[260px]">
                  <span className="inline-flex items-center gap-2 text-sm font-normal text-black">
                    <Globe className="size-4 text-muted-foreground" />
                    {t("workspace.blacklistPage.table.website")}
                  </span>
                </TableHead>
                <TableHead className="h-10 px-4 w-[180px]">
                  <span className="inline-flex items-center gap-2 text-sm font-normal text-black">
                    <Calendar className="size-4 text-muted-foreground" />
                    {t("workspace.blacklistPage.table.createdAt")}
                  </span>
                </TableHead>
                <TableHead className="h-10 px-4 w-[120px] text-right">
                  <span className="inline-flex items-center justify-end gap-2 text-sm font-normal text-black">
                    <Settings className="size-4 text-muted-foreground" />
                    {t("workspace.blacklistPage.table.actions")}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="px-4 py-6 text-center text-sm text-muted-foreground"
                  >
                    {t("workspace.blacklistPage.subtitle")}
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id} className="border-b border-[#dfdfdf]">
                    <TableCell className="px-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-black">{entry.company_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4">
                      {entry.website ? (
                        <a
                          href={entry.website}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline break-all"
                        >
                          {entry.website}
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-4 text-sm text-[#7e7e7e]">
                      {formatDate(entry.created_at)}
                    </TableCell>
                    <TableCell className="px-4 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget(entry)}
                        disabled={isSubmitting || isDeleting}
                      >
                        {t("workspace.blacklistPage.deleteDialog.confirm")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(next) => !next && setOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("workspace.blacklistPage.addDialog.title")}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("workspace.blacklistPage.addDialog.companyNameLabel")}
              </label>
              <Input
                {...form.register("company_name")}
                placeholder={t("workspace.blacklistPage.addDialog.companyNamePlaceholder")}
                disabled={isSubmitting}
              />
              {form.formState.errors.company_name && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.company_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("workspace.blacklistPage.addDialog.websiteLabel")}
              </label>
              <Input
                {...form.register("website")}
                placeholder={t("workspace.blacklistPage.addDialog.websitePlaceholder")}
                disabled={isSubmitting}
              />
              {form.formState.errors.website && (
                <p className="text-xs text-destructive">{form.formState.errors.website.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                {t("workspace.blacklistPage.addDialog.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {t("workspace.blacklistPage.addDialog.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteTarget} onOpenChange={(next) => !next && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("workspace.blacklistPage.deleteDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("workspace.blacklistPage.deleteDialog.description", {
                name: deleteTarget?.company_name ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              {t("workspace.blacklistPage.addDialog.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting}
              onClick={async () => {
                if (!deleteTarget) return;
                setIsDeleting(true);
                try {
                  await deleteBlacklistServer({
                    data: {
                      workspaceId,
                      blacklistId: deleteTarget.id,
                    },
                  });
                  setEntries((prev) => prev.filter((item) => item.id !== deleteTarget.id));
                  toast.success(t("workspace.blacklistPage.deleteDialog.success"));
                  setDeleteTarget(null);
                } catch (error) {
                  const message = extractApiErrorMessage(
                    error,
                    t("workspace.blacklistPage.deleteDialog.error"),
                  );
                  toast.error(message);
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              {t("workspace.blacklistPage.deleteDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
